import { useMemo, useRef } from "react"
import Button from "../../../components/ui/Button"
import MermaidDiagram from "../../../components/MermaidDiagram"

function slugify (title, num) {
  const base = String(title || "section")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
  return `sec-${num}-${base || "part"}`
}

function isAbstractSection (title) {
  const raw = String(title || '').trim()
  const t = raw.toLowerCase()
  return /^abstract$/i.test(raw) || (t.includes('abstract') && !t.includes('chapter'))
}

function isOwnPageSection (title) {
  const raw = String(title || '').trim()
  const t = raw.toLowerCase()
  if (isAbstractSection(raw)) return true
  if (/\breferences\b|\bbibliography\b/i.test(t)) return true
  if (/table\s+of\s+contents|list\s+of\s+contents|list\s+of\s+figures|list\s+of\s+tables/i.test(t)) return true
  if (/^aims?\s+and\s+objectives?$/i.test(raw)) return true
  return false
}

function isSubheadingLine (line) {
  const trimmed = String(line || '').trim()
  if (!trimmed) return false
  if (/^\d+\.\d+(\.\d+)?\.?\s+\S/.test(trimmed)) return true
  if (/^aims?\s+and\s+objectives?$/i.test(trimmed)) return true
  if (/^summary\s+of\s+(the\s+)?(review|literature)/i.test(trimmed)) return true
  return false
}

function isMarkdownTableSeparator (line) {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(String(line || '').trim())
}

function isMarkdownTableRow (line) {
  const trimmed = String(line || '').trim()
  if (isMarkdownTableSeparator(trimmed)) return false
  return trimmed.includes('|') && trimmed.replace(/\|/g, '').trim().length > 0
}

function parseMarkdownTableRows (lines, startIndex) {
  const rows = []
  let i = startIndex
  while (i < lines.length) {
    const trimmed = String(lines[i] || '').trim()
    if (!trimmed) break
    if (isMarkdownTableSeparator(trimmed)) {
      i++
      continue
    }
    if (!isMarkdownTableRow(lines[i])) break
    const cells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim())
    rows.push(cells)
    i++
  }
  return { rows, nextIndex: i }
}

function renderMarkdownTable (rows, key) {
  if (!rows.length) return null
  const colCount = Math.max(...rows.map((r) => r.length))
  return (
    <div key={key} className="my-4 overflow-x-auto">
      <table className="w-full border-collapse border border-slate-300 text-[12pt]">
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={`${key}-row-${rowIdx}`}>
              {Array.from({ length: colCount }).map((_, colIdx) => (
                <td
                  key={`${key}-cell-${rowIdx}-${colIdx}`}
                  className={`border border-slate-300 px-3 py-2 align-top ${rowIdx === 0 ? 'font-bold' : ''}`}
                >
                  {row[colIdx] || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function renderContentWithDiagrams (text, diagrams, { italicBody = false } = {}) {
  const safeText = String(text || '')
  const lines = safeText.split('\n')
  const safeDiagrams = Array.isArray(diagrams) ? diagrams : []
  let di = 0

  const elements = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = String(line || '').trim()

    if (isMarkdownTableRow(line) && (i + 1 < lines.length && isMarkdownTableSeparator(lines[i + 1]))) {
      const { rows, nextIndex } = parseMarkdownTableRows(lines, i)
      const table = renderMarkdownTable(rows, `table-${i}`)
      if (table) elements.push(table)
      i = nextIndex - 1
      continue
    }

    if (/^(Diagram|Figure)\b/i.test(trimmed)) {
      const d = safeDiagrams[di]
      if (!d?.code) {
        elements.push(
          <span key={`line-${i}`}>
            {line}
            {i < lines.length - 1 ? <br /> : null}
          </span>
        )
        continue
      }

      elements.push(
        <div key={`diag-${i}-${di}`} className="mt-6 border-t border-slate-100 pt-6 print:hidden">
          <p className="mb-2 font-sans text-sm font-semibold text-slate-700">{d?.name || trimmed}</p>
          <MermaidDiagram code={d.code} />
        </div>
      )
      di++
      continue
    }

    if (isSubheadingLine(trimmed)) {
      elements.push(
        <p key={`sub-${i}`} className="mt-4 font-bold">
          {line}
        </p>
      )
      continue
    }

    elements.push(
      <span key={`line-${i}`} className={italicBody ? 'italic' : undefined}>
        {line}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    )
  }

  while (di < safeDiagrams.length) {
    const d = safeDiagrams[di]
    elements.push(
      <div key={`fallback-diag-${di}`} className="mt-6 border-t border-slate-100 pt-6 print:hidden">
        <p className="mb-2 font-sans text-sm font-semibold text-slate-700">{d?.name || `Diagram ${di + 1}`}</p>
        {d?.code ? <MermaidDiagram code={d.code} /> : null}
      </div>
    )
    di++
  }

  return elements
}

/**
 * Word-style single scroll: full project documentation with TOC and print-friendly layout.
 */
const FullDocumentationView = ({
  doc,
  setPage,
  setSelectedDoc,
  setSelectedChapter,
  onBack,
  onOpenChapter,
  onRegenerateAll,
  regenLoading = false,
  onExportPdf,
  onExportDocx,
  downloading = ""
}) => {
  const mainRef = useRef(null)

  const projectTitle = doc?.project?.title || "Project documentation"
  const chapters = useMemo(() => {
    const list = doc?.chapters ? [...doc.chapters] : []
    return list.sort((a, b) => (a.chapterNumber || 0) - (b.chapterNumber || 0))
  }, [doc?.chapters])

  const scrollToId = (id) => {
    const el = document.getElementById(id)
    el?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const openSection = (ch) => {
    if (typeof onOpenChapter === "function") {
      onOpenChapter(ch)
      return
    }
    if (!doc || !ch) return
    if (typeof setSelectedDoc === "function") setSelectedDoc(doc)
    setSelectedChapter(ch)
    setPage("Chapter")
  }

  const appendixChapter = chapters.find((ch) => /appendix/i.test(String(ch?.title || "")))
  const referencesChapter = chapters.find((ch) => /references?/i.test(String(ch?.title || "")))

  if (!doc) {
    return (
      <div className="p-4">
        <Button size="sm" variant="outline" onClick={() => (typeof onBack === "function" ? onBack() : setPage("Project"))}>Back</Button>
        <p className="mt-2 text-slate-600">No documentation loaded.</p>
      </div>
    )
  }

  return (
    <div className="flex w-full min-h-0 flex-col gap-4 lg:flex-row lg:gap-8">
      <aside className="w-full shrink-0 print:hidden lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:w-56 lg:min-w-[13rem] lg:overflow-y-auto">
        <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Document map</p>
          <nav className="mt-3 space-y-1" aria-label="Table of contents">
            {chapters.map((ch) => {
              const id = slugify(ch.title, ch.chapterNumber)
              return (
                <button
                  key={ch._id || id}
                  type="button"
                  onClick={() => scrollToId(id)}
                  className="flex w-full rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 transition hover:bg-slate-100"
                >
                  <span className="mr-2 tabular-nums text-slate-400">{ch.chapterNumber}.</span>
                  <span className="min-w-0 flex-1 leading-snug">{ch.title}</span>
                </button>
              )
            })}
            <div className="mt-2 border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={() => referencesChapter && scrollToId(slugify(referencesChapter.title, referencesChapter.chapterNumber))}
                disabled={!referencesChapter}
                className="flex w-full rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="min-w-0 flex-1 leading-snug">References</span>
              </button>
              <button
                type="button"
                onClick={() => appendixChapter && scrollToId(slugify(appendixChapter.title, appendixChapter.chapterNumber))}
                disabled={!appendixChapter}
                className="flex w-full rounded-lg px-2 py-1.5 text-left text-sm text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="min-w-0 flex-1 leading-snug">Appendix</span>
              </button>
            </div>
          </nav>
          <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4">
            <Button size="sm" variant="outline" className="w-full" onClick={onExportPdf} disabled={!doc?._id || !!downloading || typeof onExportPdf !== "function"}>
              {downloading === "pdf" ? "Exporting PDF..." : "Export as PDF"}
            </Button>
            <Button size="sm" variant="outline" className="w-full" onClick={onExportDocx} disabled={!doc?._id || !!downloading || typeof onExportDocx !== "function"}>
              {downloading === "docx" ? "Exporting Word..." : "Export as Word"}
            </Button>
            <Button size="sm" variant="ghost" className="w-full" onClick={() => (typeof onBack === "function" ? onBack() : setPage("Project"))}>
              Back to projects
            </Button>
          </div>
        </div>
      </aside>

      <div
        ref={mainRef}
        className="min-w-0 flex-1 rounded-2xl border border-slate-200/80 bg-white px-5 py-8 shadow-soft print:border-0 print:shadow-none sm:px-10 sm:py-12 print:font-[Times_New_Roman]"
      >
        <header className="mb-10 border-b border-slate-200 pb-6 print:hidden">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800/90">Full documentation</p>
              <h1 className="mt-1 font-serif text-2xl font-bold leading-tight text-slate-900 sm:text-3xl">
                {projectTitle}
              </h1>
            </div>
            {typeof onRegenerateAll === "function" ? (
              <Button size="sm" loading={regenLoading} disabled={regenLoading} onClick={onRegenerateAll}>
                Regenerate all
              </Button>
            ) : null}
          </div>
        </header>

        <div className="mb-10 hidden border-b border-slate-300 pb-4 text-center font-serif print:block">
          <h1 className="text-2xl font-bold text-slate-900">{projectTitle}</h1>
        </div>

        <article className="space-y-12 text-[12pt] leading-[1.75] text-slate-900" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
          {chapters.map((ch) => {
            const id = slugify(ch.title, ch.chapterNumber)
            const ownPage = isOwnPageSection(ch.title)
            return (
              <section
                key={ch._id || id}
                id={id}
                className={`scroll-mt-24 print:mb-8 ${ownPage ? 'print:break-before-page' : ''}`}
              >
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2 print:mb-3">
                  <h2 className="w-full text-center text-[14pt] font-bold tracking-tight text-slate-900">
                    {ch.chapterNumber}. {ch.title}
                  </h2>
                  <Button size="sm" variant="outline" className="print:hidden ml-auto" onClick={() => openSection(ch)}>
                    Edit this section
                  </Button>
                </div>
                <div className="text-justify">
                  {renderContentWithDiagrams(
                    ch.content,
                    ch?.diagrams?.length ? ch.diagrams : (ch?.diagram ? [{ name: "Diagram", code: ch.diagram }] : []),
                    { italicBody: isAbstractSection(ch.title) }
                  )}
                </div>
              </section>
            )
          })}
        </article>
      </div>
    </div>
  )
}

export default FullDocumentationView
